import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, BellOff, Check, History as HistoryIcon, Lock, ShieldCheck } from 'lucide-react';

import { ErrorState, Skeleton } from '@/components/chassis/States';
import { useT } from '@/i18n';
import { cn } from '@/lib/utils';
import { toast } from '@/stores/toast.store';
import { chatNotifyApi } from '@/features/chat-notify/chat-notify.api';
import {
  CHAT_RULE_KEYS,
  DAY_KEYS,
  ESC_DEFS,
  GROUP_DEFAULTS,
  GROUP_KEYS,
  NOTIFY_CHANNELS,
  NOTIFY_EVENTS,
  NOTIFY_ROLES,
  effectiveCell,
  pruneMatrix,
  type ChatNotifyValues,
  type ChatRuleKey,
  type DayKey,
  type MatrixCellOverride,
  type NotifyMatrix,
  type NotifyRole,
} from '@/features/chat-notify/catalog';

/**
 * F5-R6c · Admin 14 «Chat y notificaciones» — dibujo del CEO (2026-08-28).
 *
 * El grupo que faltaba en Configuración: qué avisa el sistema, a quién, por
 * dónde y cuándo — y las reglas del chat. Una matriz por rol en vez de 40
 * conmutadores sueltos. Fidelidad al dibujo:
 *   · 4 pestañas de 48px con subrayado teal de 2px;
 *   · matriz de 6 columnas que NO se parte nunca (min-w 900px + scroll
 *     horizontal propio — el único del sistema, y el dibujo dice por qué);
 *   · casillas de 44×44 (el botón entero es el área táctil) con aria-pressed
 *     y aria-label evento+canal+rol; celdas bloqueadas con guion y title;
 *   · chips de prioridad (3, cicla) y recordatorio (4, cicla), rojo SOLO en
 *     la crítica, ámbar en recordatorios activos;
 *   · chip verde con el TYPE REAL leído del código, chip gris «nuevo» si el
 *     evento es propuesto — nadie confunde lo que existe con lo que falta;
 *   · si un rol queda con 0 eventos en campana, la pantalla lo dice
 *     (aria-live) — el dibujo resuelve así la pregunta del uploader (F-12);
 *     APAGAR la campana de verdad en su header es del server y sube al CEO;
 *   · guardar inerte sin cambios, dice cuántos hay; historial con
 *     antes → después y actor; cada guardado avisa al equipo por la campana.
 *
 * CABLEADO REAL: GET/POST/GET history de /admin/chat-notify-config
 * (chat-notify.controller.ts, claves de la migración 080). Los emisores de
 * hoy todavía no leen la matriz — la pantalla lo dice con un aviso, porque
 * el propio dibujo declara ese lado del server como «lo que hay que
 * construir».
 */

type TabId = 'events' | 'quiet' | 'chat' | 'history';
type PrioIdx = 0 | 1 | 2;
type RemindIdx = 0 | 1 | 2 | 3;

const LIST_KEY = ['admin', 'chat-notify'] as const;
const HIST_KEY = ['admin', 'chat-notify', 'history'] as const;

const HHMM_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

/** El server manda {key, value}[]; acá se funde con los defaults del dibujo. */
function toValues(items: Array<{ key: string; value: unknown }>): ChatNotifyValues {
  const map = new Map(items.map((i) => [i.key, i.value]));
  const v = { ...GROUP_DEFAULTS };
  const pick = <K extends keyof ChatNotifyValues>(key: K, guard: (x: unknown) => boolean): void => {
    const raw = map.get(key);
    if (raw !== undefined && guard(raw)) (v as Record<string, unknown>)[key] = raw;
  };
  pick('notify_matrix', (x) => !!x && typeof x === 'object' && !Array.isArray(x));
  pick('notify_quiet_start', (x) => typeof x === 'string' && HHMM_RE.test(x));
  pick('notify_quiet_end', (x) => typeof x === 'string' && HHMM_RE.test(x));
  pick('notify_days', (x) => !!x && typeof x === 'object' && !Array.isArray(x));
  pick('notify_dnd_in_call', (x) => typeof x === 'boolean');
  pick('notify_esc_claim_expired_min', (x) => typeof x === 'number');
  pick('notify_esc_followup_overdue_days', (x) => typeof x === 'number');
  pick('notify_esc_preauth_unsigned_hours', (x) => typeof x === 'number');
  pick('chat_rules', (x) => !!x && typeof x === 'object' && !Array.isArray(x));
  pick('chat_retention_days', (x) => typeof x === 'number');
  // Días y reglas llegan parciales de una edición vieja → completar con defaults.
  v.notify_days = { ...GROUP_DEFAULTS.notify_days, ...(v.notify_days as object) };
  v.chat_rules = { ...GROUP_DEFAULTS.chat_rules, ...(v.chat_rules as object), anyoneSupervisor: true };
  return v;
}

/** Cuenta los cambios HOJA entre lo vivo y el borrador (el botón los dice). */
function countDirty(saved: ChatNotifyValues, draft: ChatNotifyValues): number {
  let n = 0;
  for (const role of NOTIFY_ROLES) {
    for (const ev of NOTIFY_EVENTS) {
      if (!ev.roles.includes(role)) continue;
      const a = effectiveCell(ev, saved.notify_matrix, role);
      const b = effectiveCell(ev, draft.notify_matrix, role);
      for (const c of NOTIFY_CHANNELS) if (a[c] !== b[c]) n++;
      if (a.prio !== b.prio) n++;
      if (a.remind !== b.remind) n++;
    }
  }
  if (saved.notify_quiet_start !== draft.notify_quiet_start) n++;
  if (saved.notify_quiet_end !== draft.notify_quiet_end) n++;
  for (const d of DAY_KEYS) if (saved.notify_days[d] !== draft.notify_days[d]) n++;
  if (saved.notify_dnd_in_call !== draft.notify_dnd_in_call) n++;
  if (saved.notify_esc_claim_expired_min !== draft.notify_esc_claim_expired_min) n++;
  if (saved.notify_esc_followup_overdue_days !== draft.notify_esc_followup_overdue_days) n++;
  if (saved.notify_esc_preauth_unsigned_hours !== draft.notify_esc_preauth_unsigned_hours) n++;
  for (const k of CHAT_RULE_KEYS) if (saved.chat_rules[k] !== draft.chat_rules[k]) n++;
  if (saved.chat_retention_days !== draft.chat_retention_days) n++;
  return n;
}

export function ChatNotificationsPage(): JSX.Element {
  const t = useT();
  const qc = useQueryClient();
  const [tab, setTab] = useState<TabId>('events');
  const [role, setRole] = useState<NotifyRole>('agent');
  const [draft, setDraft] = useState<ChatNotifyValues | null>(null);

  const listQ = useQuery({ queryKey: LIST_KEY, queryFn: chatNotifyApi.list, staleTime: 30_000 });

  const saved = useMemo(
    () => (listQ.data ? toValues(listQ.data.items) : null),
    [listQ.data],
  );

  // El borrador nace del server; una recarga con cambios encima NO los pisa.
  useEffect(() => {
    if (saved && draft === null) setDraft(saved);
  }, [saved, draft]);

  const dirty = saved && draft ? countDirty(saved, draft) : 0;

  const quietValid =
    !!draft &&
    HHMM_RE.test(draft.notify_quiet_start) &&
    HHMM_RE.test(draft.notify_quiet_end) &&
    draft.notify_quiet_start < draft.notify_quiet_end;

  const saveMut = useMutation({
    mutationFn: (changes: Array<{ key: string; value: unknown }>) => chatNotifyApi.save(changes),
    onSuccess: () => {
      setDraft(null); // el próximo dato del server re-siembra el borrador
      void qc.invalidateQueries({ queryKey: LIST_KEY });
      void qc.invalidateQueries({ queryKey: HIST_KEY });
      toast.success(t('f5r6c.save.ok'));
    },
    onError: (e) => toast.error(t('f5r6c.save.err'), (e as Error).message),
  });

  const doSave = (): void => {
    if (!saved || !draft || dirty === 0 || !quietValid || saveMut.isPending) return;
    const changes: Array<{ key: string; value: unknown }> = [];
    for (const key of GROUP_KEYS) {
      const a = saved[key];
      const b = key === 'notify_matrix' ? pruneMatrix(draft.notify_matrix) : draft[key];
      const same = JSON.stringify(key === 'notify_matrix' ? pruneMatrix(saved.notify_matrix) : a) === JSON.stringify(b);
      if (!same) changes.push({ key, value: b });
    }
    if (changes.length === 0) return;
    saveMut.mutate(changes);
  };

  if (listQ.isLoading || (!saved && !listQ.isError)) {
    return (
      <div className="space-y-3" aria-busy="true">
        <Skeleton className="h-[92px] w-full" />
        <Skeleton className="h-[46px] w-full" />
        <Skeleton className="h-[420px] w-full" />
      </div>
    );
  }
  if (listQ.isError || !saved || !draft) {
    return (
      <ErrorState
        icon={<AlertCircle className="h-6 w-6" aria-hidden />}
        title={t('f5r6c.err.title')}
        body={t('f5r6c.err.body')}
        action={{ label: t('f5r6c.hist.retry'), onClick: () => void listQ.refetch() }}
      />
    );
  }

  const saveLabel = saveMut.isPending
    ? t('f5r6c.save.saving')
    : dirty === 0
      ? t('f5r6c.save.none')
      : dirty === 1
        ? t('f5r6c.save.one')
        : t('f5r6c.save.n', { n: dirty });
  const saveDisabled = dirty === 0 || !quietValid || saveMut.isPending;

  const TABS: Array<{ id: TabId; label: string }> = [
    { id: 'events', label: t('f5r6c.tab.events') },
    { id: 'quiet', label: t('f5r6c.tab.quiet') },
    { id: 'chat', label: t('f5r6c.tab.chat') },
    { id: 'history', label: t('f5r6c.tab.history') },
  ];

  return (
    <div
      className="bg-nav-soft border-[1.5px] border-nav-card rounded-[14px] pb-[30px] overflow-hidden animate-fade-up motion-reduce:animate-none"
      data-r6c="root"
    >
      {/* ——— Encabezado del grupo: título, chip seguro, guardar, pestañas ——— */}
      <div className="bg-white border-b-[1.5px] border-nav-card px-4 sm:px-7 pt-[22px]">
        <div className="flex items-start justify-between gap-5 flex-wrap mb-[18px]">
          <div className="min-w-0">
            <p className="m-0 mb-[5px] font-head text-xl font-extrabold tracking-[-0.02em] text-nav-navy">
              {t('f5r6c.title')}
            </p>
            <p className="m-0 text-[13.5px] leading-normal text-nav-slate">{t('f5r6c.sub')}</p>
          </div>
          <div className="flex items-center gap-2.5 flex-wrap">
            <span className="inline-flex items-center gap-2 bg-nav-sageTint text-nav-sageText border border-nav-sage rounded-full py-[7px] px-3 font-head text-[11.5px] font-bold">
              <ShieldCheck className="h-3.5 w-3.5 flex-none" aria-hidden />
              {t('f5r6c.safeChip')}
            </span>
            <button
              type="button"
              onClick={doSave}
              disabled={saveDisabled}
              data-r6c="save"
              className={cn(
                'inline-flex items-center min-h-[44px] px-[18px] rounded-lg border-[1.5px] font-head text-[13px] font-bold transition-colors',
                saveDisabled
                  ? 'bg-nav-hair text-nav-faint border-nav-card cursor-not-allowed'
                  : 'bg-nav-navy text-white border-nav-navy hover:bg-nav-navyHover hover:border-nav-navyHover',
              )}
            >
              {saveLabel}
            </button>
          </div>
        </div>

        <div className="flex items-stretch gap-0.5 flex-wrap" role="tablist">
          {TABS.map((tb) => {
            const on = tab === tb.id;
            return (
              <button
                key={tb.id}
                type="button"
                role="tab"
                aria-selected={on}
                aria-current={on ? 'page' : undefined}
                onClick={() => setTab(tb.id)}
                className="relative inline-flex items-center flex-none min-h-[48px] px-3.5 hover:opacity-80 transition-opacity"
                data-r6c={`tab-${tb.id}`}
              >
                <span
                  className={cn(
                    'font-head text-[13.5px] whitespace-nowrap transition-colors',
                    on ? 'font-bold text-nav-navy' : 'font-semibold text-nav-slate',
                  )}
                >
                  {tb.label}
                </span>
                <span
                  aria-hidden
                  className={cn(
                    'absolute left-3.5 right-3.5 bottom-0 h-0.5 rounded-sm bg-nav-teal origin-left transition-transform duration-300',
                    on ? 'scale-x-100' : 'scale-x-0',
                  )}
                />
              </button>
            );
          })}
        </div>
      </div>

      <div className="px-4 sm:px-7 pt-[26px]">
        {tab === 'events' && (
          <EventsTab role={role} setRole={setRole} draft={draft} setDraft={setDraft} />
        )}
        {tab === 'quiet' && <QuietTab draft={draft} setDraft={setDraft} quietValid={quietValid} />}
        {tab === 'chat' && <ChatTab draft={draft} setDraft={setDraft} />}
        {tab === 'history' && <HistoryTab />}
      </div>
    </div>
  );
}

/* ————————————————————— Eventos y canales ————————————————————— */

function EventsTab(props: {
  role: NotifyRole;
  setRole: (r: NotifyRole) => void;
  draft: ChatNotifyValues;
  setDraft: React.Dispatch<React.SetStateAction<ChatNotifyValues | null>>;
}): JSX.Element {
  const { role, setRole, draft, setDraft } = props;
  const t = useT();

  const roleLabel = t(`f5r6c.role.${role}` as Parameters<typeof t>[0]);
  const rows = NOTIFY_EVENTS.filter((e) => e.roles.includes(role));

  let bellCount = 0;
  let urgentCount = 0;
  for (const e of rows) {
    const cell = effectiveCell(e, draft.notify_matrix, role);
    if (cell.campana === true) bellCount++;
    if (cell.prio === 2) urgentCount++;
  }
  const bellOff = bellCount === 0;

  const setCell = (evKey: string, patch: MatrixCellOverride): void => {
    setDraft((d) => {
      if (!d) return d;
      const m: NotifyMatrix = { ...d.notify_matrix };
      const evs = { ...(m[role] ?? {}) };
      evs[evKey] = { ...(evs[evKey] ?? {}), ...patch };
      m[role] = evs;
      return { ...d, notify_matrix: m };
    });
  };

  return (
    <>
      <div className="flex items-center gap-3 flex-wrap mb-[18px]">
        <span className="font-head text-[10.5px] font-bold tracking-[.12em] uppercase text-nav-slate">
          {t('f5r6c.role')}
        </span>
        <div
          className="flex items-center gap-0.5 bg-white border-[1.5px] border-nav-card rounded-[10px] p-[3px] flex-wrap"
          role="group"
          aria-label={t('f5r6c.role')}
        >
          {NOTIFY_ROLES.map((r) => {
            const on = role === r;
            return (
              <button
                key={r}
                type="button"
                aria-pressed={on}
                onClick={() => setRole(r)}
                data-r6c={`role-${r}`}
                className={cn(
                  'font-head font-bold text-[12.5px] min-h-[44px] px-[15px] rounded-lg transition-colors',
                  on ? 'bg-nav-navy text-white hover:bg-nav-navyHover' : 'text-nav-slate hover:bg-nav-hair hover:text-nav-navy',
                )}
              >
                {t(`f5r6c.role.${r}` as Parameters<typeof t>[0])}
              </button>
            );
          })}
        </div>
        <span className="font-body text-[12.5px] text-nav-slate tabular-nums">
          {t('f5r6c.roleNote', { n: rows.length, bell: bellCount, urgent: urgentCount })}
        </span>
        <span aria-live="polite" className="sr-only">
          {bellOff
            ? t('f5r6c.bellAnnounce.off', { role: roleLabel })
            : t('f5r6c.bellAnnounce.on', { role: roleLabel, bell: bellCount })}
        </span>
      </div>

      {bellOff && (
        <div
          className="flex items-start gap-2.5 bg-nav-amberTint border-[1.5px] border-nav-amber rounded-xl py-[15px] px-[17px] mb-4 font-body text-[13px] leading-relaxed text-nav-amberText"
          data-r6c="bell-off-warn"
        >
          <BellOff className="h-[17px] w-[17px] flex-none mt-0.5" aria-hidden />
          <span>
            <b>{t('f5r6c.bellWarn.b')}</b> {t('f5r6c.bellWarn.rest')}
          </span>
        </div>
      )}

      <div className="bg-white border-[1.5px] border-nav-card rounded-xl overflow-hidden">
        {/* Las seis columnas no se parten NUNCA: min-w 900 + scroll propio —
            el único scroll horizontal del sistema, y el dibujo dice por qué. */}
        <div className="overflow-x-auto">
          <div className="min-w-[900px]">
            <div className="flex items-center gap-2.5 flex-nowrap min-h-[46px] py-2 px-5 bg-nav-softer border-b border-nav-hair">
              <span className="flex-1 min-w-[200px] font-head text-[10.5px] font-bold tracking-[.08em] uppercase text-nav-slate">
                {t('f5r6c.col.event')}
              </span>
              {NOTIFY_CHANNELS.map((c) => (
                <span key={c} className="flex-none w-24 text-center font-head text-[10.5px] font-bold tracking-[.08em] uppercase text-nav-slate">
                  {t(`f5r6c.chan.${c}` as Parameters<typeof t>[0])}
                  <span className="block mt-[3px] font-body text-[10px] font-medium tracking-normal normal-case text-nav-faint">
                    {t(`f5r6c.chan.${c}.sub` as Parameters<typeof t>[0])}
                  </span>
                </span>
              ))}
              <span className="flex-none w-32 text-right font-head text-[10.5px] font-bold tracking-[.08em] uppercase text-nav-slate">
                {t('f5r6c.col.prio')}
              </span>
              <span className="flex-none w-[132px] text-right font-head text-[10.5px] font-bold tracking-[.08em] uppercase text-nav-amberText">
                {t('f5r6c.col.remind')}
              </span>
            </div>

            {rows.map((e, i) => (
              <MatrixRow
                key={e.k}
                def={e}
                first={i === 0}
                roleLabel={roleLabel}
                cell={effectiveCell(e, draft.notify_matrix, role)}
                setCell={setCell}
              />
            ))}
          </div>
        </div>

        <div className="py-3.5 px-5 border-t border-nav-hair bg-nav-softer space-y-1.5">
          <p className="m-0 text-[12.5px] leading-relaxed text-nav-slate">{t('f5r6c.matrixFoot')}</p>
          <p className="m-0 text-[12.5px] leading-relaxed text-nav-amberText" data-r6c="matrix-pending">
            {t('f5r6c.matrixPending')}
          </p>
        </div>
      </div>
    </>
  );
}

function MatrixRow(props: {
  def: (typeof NOTIFY_EVENTS)[number];
  first: boolean;
  roleLabel: string;
  cell: ReturnType<typeof effectiveCell>;
  setCell: (evKey: string, patch: MatrixCellOverride) => void;
}): JSX.Element {
  const { def, first, roleLabel, cell, setCell } = props;
  const t = useT();
  const label = t(`f5r6c.ev.${def.k}.label` as Parameters<typeof t>[0]);
  const prioLabel = t(`f5r6c.prio.${cell.prio}` as Parameters<typeof t>[0]);
  const remindLabel = t(`f5r6c.remind.${cell.remind}` as Parameters<typeof t>[0]);

  return (
    <div
      className={cn(
        'flex items-center gap-2.5 flex-nowrap min-h-[80px] py-[13px] px-5 bg-white',
        !first && 'border-t border-nav-hair',
      )}
      data-r6c={`row-${def.k}`}
    >
      <span className="flex-1 min-w-[200px]">
        <span className="block font-head text-[13.5px] font-bold text-nav-navy">{label}</span>
        <span className="block mt-[3px] text-[12.5px] leading-normal text-nav-slate">
          {t(`f5r6c.ev.${def.k}.rule` as Parameters<typeof t>[0])}
        </span>
        <span
          className={cn(
            'inline-block mt-1.5 rounded-full py-0.5 px-2 font-head text-[9.5px] font-bold tracking-[.04em] border',
            def.liveType
              ? 'bg-nav-sageTint text-nav-sageText border-nav-sage'
              : 'bg-nav-hair text-nav-slate border-nav-card',
          )}
        >
          {def.liveType ?? t('f5r6c.tag.nuevo')}
        </span>
      </span>

      {NOTIFY_CHANNELS.map((c) => {
        const v = cell[c];
        if (v === null) {
          // Bloqueada, no ausente: guion con la razón en title (regla del dibujo).
          return (
            <span key={c} className="flex-none w-24 flex items-center justify-center min-h-[44px]">
              <span
                className="font-head text-[15px] font-bold text-nav-faint cursor-help"
                title={t(c === 'correo' ? 'f5r6c.blocked.correo' : 'f5r6c.blocked.chat')}
              >
                —
              </span>
            </span>
          );
        }
        const chanLabel = t(`f5r6c.chan.${c}` as Parameters<typeof t>[0]);
        return (
          <span key={c} className="flex-none w-24 flex items-center justify-center min-h-[44px]">
            <button
              type="button"
              aria-pressed={v}
              aria-label={t('f5r6c.cell.aria', { event: label, channel: chanLabel, role: roleLabel })}
              onClick={() => setCell(def.k, { [c]: !v } as MatrixCellOverride)}
              data-r6c={`cell-${def.k}-${c}`}
              className={cn(
                'inline-flex items-center justify-center w-[44px] h-[44px] rounded-[10px] border-[1.5px] transition-colors hover:border-nav-teal',
                v ? 'bg-nav-teal border-nav-teal' : 'bg-white border-nav-card',
              )}
            >
              {v && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3.2} aria-hidden />}
            </button>
          </span>
        );
      })}

      <span className="flex-none w-32 flex items-center justify-end min-h-[44px]">
        <button
          type="button"
          aria-label={t('f5r6c.prio.aria', { event: label, value: prioLabel, role: roleLabel })}
          onClick={() => setCell(def.k, { prio: ((cell.prio + 1) % 3) as PrioIdx })}
          data-r6c={`prio-${def.k}`}
          className={cn(
            'inline-flex flex-col items-start min-h-[44px] py-1.5 px-3 rounded-[10px] border-[1.5px] transition-colors text-left',
            cell.prio === 0 && 'bg-nav-hair text-nav-slate border-nav-card hover:border-nav-slate',
            cell.prio === 1 && 'bg-nav-tealTint text-nav-tealDark border-nav-teal/30 hover:border-nav-tealDark',
            cell.prio === 2 && 'bg-nav-redTint text-nav-red border-nav-red/30 hover:border-nav-red',
          )}
        >
          <span className="block font-head text-xs font-bold leading-tight">{prioLabel}</span>
          <span className="block mt-0.5 font-body text-[10.5px] font-medium leading-snug">
            {t(`f5r6c.prio.${cell.prio}.note` as Parameters<typeof t>[0])}
          </span>
        </button>
      </span>

      <span className="flex-none w-[132px] flex items-center justify-end min-h-[44px]">
        <button
          type="button"
          aria-label={t('f5r6c.remind.aria', { event: label, value: remindLabel, role: roleLabel })}
          onClick={() => setCell(def.k, { remind: ((cell.remind + 1) % 4) as RemindIdx })}
          data-r6c={`remind-${def.k}`}
          className={cn(
            'inline-flex flex-col items-start min-h-[44px] py-1.5 px-3 rounded-[10px] border-[1.5px] transition-colors text-left',
            cell.remind === 0
              ? 'bg-white border-nav-card text-nav-slate hover:border-nav-faint'
              : 'bg-nav-amberTint border-nav-amber text-nav-amberText hover:border-nav-amberText',
          )}
        >
          <span className="block font-head text-xs font-bold leading-tight">{remindLabel}</span>
          <span className="block mt-0.5 font-body text-[10.5px] font-medium leading-snug">
            {t(`f5r6c.remind.${cell.remind}.note` as Parameters<typeof t>[0])}
          </span>
        </button>
      </span>
    </div>
  );
}

/* ————————————————————— Horarios y escalado ————————————————————— */

function Kicker({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <p className="m-0 mb-3.5 font-head text-[10.5px] font-bold tracking-[.14em] uppercase text-nav-teal">
      {children}
    </p>
  );
}

function Card({ children, testId }: { children: React.ReactNode; testId?: string }): JSX.Element {
  return (
    <div className="bg-white border-[1.5px] border-nav-card rounded-xl py-5 px-[22px]" data-r6c={testId}>
      {children}
    </div>
  );
}

function QuietTab(props: {
  draft: ChatNotifyValues;
  setDraft: React.Dispatch<React.SetStateAction<ChatNotifyValues | null>>;
  quietValid: boolean;
}): JSX.Element {
  const { draft, setDraft, quietValid } = props;
  const t = useT();

  const patch = (p: Partial<ChatNotifyValues>): void =>
    setDraft((d) => (d ? { ...d, ...p } : d));

  return (
    <>
      <div className="grid gap-4 mb-4 [grid-template-columns:repeat(auto-fit,minmax(330px,1fr))]">
        <Card testId="quiet-window">
          <Kicker>{t('f5r6c.quiet.k1')}</Kicker>
          <p className="m-0 mb-[18px] text-[13px] leading-relaxed text-nav-slate">{t('f5r6c.quiet.p1')}</p>
          <div className="grid gap-3.5 [grid-template-columns:repeat(auto-fit,minmax(140px,1fr))]">
            <label className="block">
              <span className="block font-head text-[10.5px] font-bold tracking-[.12em] uppercase text-nav-slate">
                {t('f5r6c.quiet.from')}
              </span>
              <input
                type="text"
                inputMode="numeric"
                value={draft.notify_quiet_start}
                onChange={(e) => patch({ notify_quiet_start: e.target.value })}
                data-r6c="quiet-from"
                className="w-full box-border h-[44px] mt-[7px] px-[13px] border-[1.5px] border-nav-card rounded-lg font-body text-sm text-nav-navy bg-white tabular-nums focus-visible:outline focus-visible:outline-2 focus-visible:outline-nav-teal"
              />
            </label>
            <label className="block">
              <span className="block font-head text-[10.5px] font-bold tracking-[.12em] uppercase text-nav-slate">
                {t('f5r6c.quiet.to')}
              </span>
              <input
                type="text"
                inputMode="numeric"
                value={draft.notify_quiet_end}
                onChange={(e) => patch({ notify_quiet_end: e.target.value })}
                data-r6c="quiet-to"
                className="w-full box-border h-[44px] mt-[7px] px-[13px] border-[1.5px] border-nav-card rounded-lg font-body text-sm text-nav-navy bg-white tabular-nums focus-visible:outline focus-visible:outline-2 focus-visible:outline-nav-teal"
              />
            </label>
          </div>
          {!quietValid && (
            <p className="mt-2.5 mb-0 text-[12.5px] font-semibold text-nav-red" data-r6c="quiet-invalid">
              {t('f5r6c.quiet.invalid')}
            </p>
          )}
          <p className="mt-3.5 mb-0 text-[12.5px] leading-normal text-nav-slate">{t('f5r6c.quiet.p2')}</p>
        </Card>

        <Card testId="quiet-days">
          <Kicker>{t('f5r6c.days.k')}</Kicker>
          <div className="flex gap-2 flex-wrap mb-4">
            {DAY_KEYS.map((d: DayKey) => {
              const on = draft.notify_days[d];
              return (
                <button
                  key={d}
                  type="button"
                  aria-pressed={on}
                  onClick={() =>
                    patch({ notify_days: { ...draft.notify_days, [d]: !on } })
                  }
                  data-r6c={`day-${d}`}
                  className={cn(
                    'min-w-[52px] min-h-[44px] px-3 rounded-lg border-[1.5px] font-head text-[12.5px] font-bold transition-colors',
                    on
                      ? 'bg-nav-navy text-white border-nav-navy hover:bg-nav-navyHover hover:border-nav-navyHover'
                      : 'bg-white text-nav-slate border-nav-card hover:text-nav-tealDark hover:border-nav-teal',
                  )}
                >
                  {t(`f5r6c.day.${d}` as Parameters<typeof t>[0])}
                </button>
              );
            })}
          </div>
          <p className="m-0 mb-[18px] text-[12.5px] leading-normal text-nav-slate">{t('f5r6c.days.p')}</p>

          <div className="flex items-center gap-3.5 flex-wrap pt-[18px] border-t border-nav-hair">
            <span className="flex-1 min-w-[180px]">
              <span className="block font-head text-[13.5px] font-bold text-nav-navy">{t('f5r6c.dnd.label')}</span>
              <span className="block mt-[3px] text-[12.5px] leading-normal text-nav-slate">{t('f5r6c.dnd.note')}</span>
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={draft.notify_dnd_in_call}
              aria-label={t('f5r6c.dnd.label')}
              onClick={() => patch({ notify_dnd_in_call: !draft.notify_dnd_in_call })}
              data-r6c="dnd"
              className={cn(
                'relative flex-none w-12 h-7 rounded-full border-[1.5px] transition-colors',
                draft.notify_dnd_in_call ? 'bg-nav-teal border-nav-teal' : 'bg-white border-nav-card',
              )}
            >
              <span
                aria-hidden
                className={cn(
                  'absolute top-[2px] left-[2px] w-[22px] h-[22px] rounded-full bg-white shadow-[0_1px_3px_rgba(18,43,68,.25)] transition-transform duration-200 motion-reduce:transition-none',
                  draft.notify_dnd_in_call ? 'translate-x-5' : 'translate-x-0',
                )}
              />
            </button>
          </div>
        </Card>
      </div>

      <Card testId="escalation">
        <Kicker>{t('f5r6c.esc.k')}</Kicker>
        <p className="m-0 mb-[18px] text-[13px] leading-relaxed text-nav-slate">{t('f5r6c.esc.p')}</p>
        <div className="flex flex-col gap-3.5">
          {ESC_DEFS.map((r) => {
            const value = draft[r.key];
            return (
              <div
                key={r.key}
                className="flex items-center gap-3.5 flex-wrap py-3.5 px-4 bg-nav-softer border-[1.5px] border-nav-card rounded-[10px]"
                data-r6c={`esc-${r.key}`}
              >
                <span className="flex-1 min-w-[220px]">
                  <span className="block font-head text-[13.5px] font-bold text-nav-navy">{t(r.labelKey)}</span>
                  <span className="block mt-[3px] text-[12.5px] leading-normal text-nav-slate">{t(r.ruleKey)}</span>
                </span>
                <div className="flex items-center gap-3 flex-none">
                  <input
                    type="range"
                    min={r.min}
                    max={r.max}
                    value={value}
                    aria-label={t(r.labelKey)}
                    onChange={(e) => patch({ [r.key]: Number(e.target.value) } as Partial<ChatNotifyValues>)}
                    className="flex-1 min-w-[140px] h-[44px] accent-nav-teal"
                  />
                  <span className="flex-none min-w-[72px] text-center bg-nav-tealTint border-[1.5px] border-nav-teal/30 rounded-lg py-[9px] font-head text-[13px] font-extrabold text-nav-tealDark tabular-nums">
                    {value} {t(r.unitKey)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </>
  );
}

/* ————————————————————— Reglas del chat ————————————————————— */

const PRIVACY_ROWS = [
  { id: 'audited', labelKey: 'f5r6c.priv.audited.label', noteKey: 'f5r6c.priv.audited.note' },
  { id: 'nomirror', labelKey: 'f5r6c.priv.nomirror.label', noteKey: 'f5r6c.priv.nomirror.note' },
  { id: 'nofiles', labelKey: 'f5r6c.priv.nofiles.label', noteKey: 'f5r6c.priv.nofiles.note' },
] as const;

function ChatTab(props: {
  draft: ChatNotifyValues;
  setDraft: React.Dispatch<React.SetStateAction<ChatNotifyValues | null>>;
}): JSX.Element {
  const { draft, setDraft } = props;
  const t = useT();

  const toggleRule = (k: ChatRuleKey): void => {
    if (k === 'anyoneSupervisor') return; // el dibujo: no se puede apagar del todo
    setDraft((d) =>
      d ? { ...d, chat_rules: { ...d.chat_rules, [k]: !d.chat_rules[k] } } : d,
    );
  };

  const retDays = draft.chat_retention_days;
  const retLabel =
    retDays >= 365
      ? t('f5r6c.ret.years', { y: Math.round((retDays / 365) * 10) / 10 })
      : t('f5r6c.ret.days', { d: retDays });

  return (
    <div className="grid gap-4 mb-4 [grid-template-columns:repeat(auto-fit,minmax(330px,1fr))]">
      <Card testId="chat-rules">
        <Kicker>{t('f5r6c.chat.k')}</Kicker>
        <div className="flex flex-col gap-[9px]">
          {CHAT_RULE_KEYS.map((k) => {
            const on = draft.chat_rules[k];
            const locked = k === 'anyoneSupervisor';
            return (
              <button
                key={k}
                type="button"
                aria-pressed={on}
                aria-disabled={locked || undefined}
                onClick={() => toggleRule(k)}
                data-r6c={`pair-${k}`}
                className={cn(
                  'flex items-start gap-[11px] w-full box-border min-h-[44px] py-[13px] px-[15px] rounded-[10px] border-[1.5px] transition-colors text-left',
                  on ? 'border-nav-teal bg-nav-tealTint/40' : 'border-nav-card bg-white',
                  locked ? 'cursor-default' : 'hover:border-nav-teal hover:bg-nav-tealTint/40',
                )}
              >
                <span
                  aria-hidden
                  className={cn(
                    'flex-none inline-flex items-center justify-center w-[22px] h-[22px] mt-px rounded-md border-[1.5px]',
                    on ? 'bg-nav-teal border-nav-teal' : 'bg-white border-nav-card',
                  )}
                >
                  {on && <Check className="h-[13px] w-[13px] text-white" strokeWidth={3.2} aria-hidden />}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block font-head text-[13.5px] font-bold text-nav-navy">
                    {t(`f5r6c.pair.${k}.label` as Parameters<typeof t>[0])}
                  </span>
                  <span className="block mt-[3px] text-[12.5px] leading-normal text-nav-slate">
                    {t(`f5r6c.pair.${k}.note` as Parameters<typeof t>[0])}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </Card>

      <div className="flex flex-col gap-4">
        <Card testId="chat-privacy">
          <Kicker>{t('f5r6c.priv.k')}</Kicker>
          <div className="flex flex-col gap-[9px]">
            {PRIVACY_ROWS.map((p) => (
              <div
                key={p.id}
                className="flex items-center gap-3.5 flex-wrap py-[13px] px-[15px] bg-nav-softer border-[1.5px] border-nav-card rounded-[10px]"
              >
                <span className="flex-1 min-w-[180px]">
                  <span className="block font-head text-[13.5px] font-bold text-nav-navy">{t(p.labelKey)}</span>
                  <span className="block mt-[3px] text-[12.5px] leading-normal text-nav-slate">{t(p.noteKey)}</span>
                </span>
                <span className="inline-flex items-center gap-[7px] flex-none min-h-[36px] px-3 bg-nav-hair border-[1.5px] border-nav-card rounded-lg font-head text-[11.5px] font-bold text-nav-slate whitespace-nowrap">
                  <Lock className="h-3.5 w-3.5 flex-none" aria-hidden />
                  {t('f5r6c.priv.always')}
                </span>
              </div>
            ))}
          </div>
          <p className="mt-4 mb-0 pt-3.5 border-t border-nav-hair text-[12.5px] leading-relaxed text-nav-slate">
            {t('f5r6c.priv.foot')}
          </p>
        </Card>

        <Card testId="chat-retention">
          <Kicker>{t('f5r6c.ret.k')}</Kicker>
          <div className="flex items-center gap-3 mb-3">
            <input
              type="range"
              min={90}
              max={2555}
              step={30}
              value={retDays}
              aria-label={t('f5r6c.ret.aria')}
              onChange={(e) =>
                props.setDraft((d) => (d ? { ...d, chat_retention_days: Number(e.target.value) } : d))
              }
              data-r6c="retention"
              className="flex-1 min-w-[140px] h-[44px] accent-nav-teal"
            />
            <span
              className={cn(
                'flex-none min-w-[84px] text-center border-[1.5px] rounded-lg py-[9px] font-head text-[13px] font-extrabold tabular-nums',
                retDays < 365
                  ? 'bg-nav-amberTint border-nav-amber text-nav-amberText'
                  : 'bg-nav-tealTint border-nav-teal/30 text-nav-tealDark',
              )}
            >
              {retLabel}
            </span>
          </div>
          <p className="m-0 text-[12.5px] leading-relaxed text-nav-slate">
            {retDays < 365 ? t('f5r6c.ret.warn') : t('f5r6c.ret.ok')}
          </p>
          <p className="mt-2 mb-0 text-[12.5px] leading-relaxed text-nav-amberText">{t('f5r6c.ret.pending')}</p>
        </Card>
      </div>
    </div>
  );
}

/* ————————————————————— Historial ————————————————————— */

function compact(v: unknown): string {
  const s = typeof v === 'string' ? v : JSON.stringify(v);
  return s.length > 80 ? `${s.slice(0, 80)}…` : s;
}

function HistoryTab(): JSX.Element {
  const t = useT();
  const q = useQuery({ queryKey: HIST_KEY, queryFn: chatNotifyApi.history, staleTime: 30_000 });

  if (q.isLoading) {
    return (
      <div className="space-y-2" aria-busy="true">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }
  if (q.isError) {
    return (
      <ErrorState
        icon={<HistoryIcon className="h-6 w-6" aria-hidden />}
        title={t('f5r6c.hist.errTitle')}
        body={t('f5r6c.hist.errBody')}
        action={{ label: t('f5r6c.hist.retry'), onClick: () => void q.refetch() }}
      />
    );
  }

  const items = q.data?.items ?? [];

  return (
    <Card testId="history">
      <Kicker>{t('f5r6c.hist.k')}</Kicker>
      {items.length === 0 ? (
        <p className="m-0 mb-3.5 text-[13px] text-nav-slate">{t('f5r6c.hist.empty')}</p>
      ) : (
        <div className="flex flex-col">
          {items.map((h, i) => (
            <div key={`${h.at}-${i}`} className="flex gap-3.5 items-start" data-r6c="hist-row">
              <span className="flex-none flex flex-col items-center self-stretch pt-[5px]">
                <span
                  aria-hidden
                  className={cn('flex-none w-2.5 h-2.5 rounded-full', i === 0 ? 'bg-nav-teal' : 'bg-nav-card')}
                />
                {i !== items.length - 1 && <span aria-hidden className="flex-1 w-0.5 min-h-[20px] bg-nav-hair" />}
              </span>
              <span className="flex-1 min-w-0 pb-[18px]">
                <span className="block font-head text-[13.5px] font-bold text-nav-navy">
                  {t(`f5r6c.key.${h.key}` as Parameters<typeof t>[0])}
                </span>
                <span className="block mt-[3px] text-[12.5px] leading-normal text-nav-slate tabular-nums">
                  {(h.actorName ?? '—') + ' · ' + new Date(h.at).toLocaleString()}
                </span>
                <span className="inline-block mt-2 bg-nav-hair text-nav-slate rounded-lg py-[5px] px-2.5 font-head text-[11.5px] font-bold tabular-nums break-all">
                  {compact(h.oldValue)} → {compact(h.newValue)}
                </span>
              </span>
            </div>
          ))}
        </div>
      )}
      <p className="m-0 pt-3.5 border-t border-nav-hair text-[12.5px] leading-relaxed text-nav-slate">
        {t('f5r6c.hist.foot')}
      </p>
    </Card>
  );
}
